import axios from 'axios'

// Create axios instance
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  timeout: 600000, // Increased to 600 seconds (10 minutes) to match backend Nginx settings
  headers: {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

// The token lives in localStorage (auth.js writes it JSON-encoded). Reading it
// here, on every request, keeps any apiClient in step with it - including the
// fresh one Vite builds on hot-reload, which used to go out with no header and
// log the developer out on every file save.
const storedToken = () => {
  try {
    const raw = localStorage.getItem('token')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// Request interceptor
apiClient.interceptors.request.use(
  config => {
    const token = storedToken()
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // Handle FormData - let axios set content-type automatically
    if (config.data instanceof FormData) {
      // Don't set Content-Type header for FormData
      // Axios will automatically set it to multipart/form-data with boundary
      delete config.headers['Content-Type']
    } else {
      // For regular JSON requests
      config.headers['Content-Type'] = 'application/json'
    }
    
    if (import.meta.env.VITE_APP_DEBUG === 'true') {
      console.log('🔄 API Request:', config.method?.toUpperCase(), config.url)
    }
    
    return config
  },
  error => {
    console.error('❌ Request Error:', error)
    return Promise.reject(error)
  }
)

// Endpoints that must handle their own 401. A wrong password on /login returns
// 401 too - clearing auth and redirecting there would wipe out the login page.
const AUTH_ENDPOINTS = ['/login', '/forgot-password', '/reset-password']

const isAuthEndpoint = (url = '') => AUTH_ENDPOINTS.some(path => url.includes(path))

// Response interceptor
apiClient.interceptors.response.use(
  response => {
    if (import.meta.env.VITE_APP_DEBUG === 'true') {
      console.log('✅ API Response:', response.status, response.config.url)
    }
    return response
  },
  error => {
    console.error('❌ Response Error:', error.response?.status, error.response?.data)

    // A 401 means the stored token is dead: auth must be cleared and the user
    // sent to /login. That is done in main.js, which listens for this event -
    // not here. Importing the store or router from this module put axios.js
    // above every page in Vite's module graph, so any page edit rebuilt this
    // client on hot-reload.
    if (error.response?.status === 401 && !isAuthEndpoint(error.config?.url)) {
      window.dispatchEvent(new CustomEvent('auth:expired'))
    }

    return Promise.reject(error)
  }
)

export default apiClient 